/*
 * @Copyright 2024-2026. Institute for Future Intelligence, Inc.
 *
 * Torsional (proper dihedral) potential:
 *   V(phi) = 0.5 * strength * (1 - cos(periodicity * phi - dihedralOffset))
 *
 * phi is the SIGNED dihedral between the planes defined by atoms (1, 2, 3)
 * and (2, 3, 4) — i.e. the angle between normals n1 = b1 × b2 and n2 = b2 × b3
 * where b1 = p2 - p1, b2 = p3 - p2, b3 = p4 - p3. Sign is conventional
 * (Bekker/Berendsen/van Gunsteren 1995): phi = atan2(b2_hat · (n1 × n2), n1 · n2).
 *
 * Analytic forces on the four atoms follow the Blondel/Karplus / GROMACS
 * decomposition, which guarantees both translational invariance
 * (Σ f_i = 0) and zero net torque about the central b2 axis.
 *
 * See also AMBER force field: http://en.wikipedia.org/wiki/AMBER
 */

import { Atom } from './Atom.ts';
import { Vector3 } from 'three';
import { GF_CONVERSION_CONSTANT, MIN_SIN_THETA } from './physicalConstants.ts';

export class TorsionalBond {
  static readonly DEFAULT_STRENGTH = 1;
  static readonly DEFAULT_PERIODICITY = 1;
  static readonly DEFAULT_DIHEDRAL_OFFSET = 0;

  /**
   * SIMPLIFIED dihedral — the unsigned angle between the (a2 → a1) and
   * (a3 → a4) vectors (treating the chain as two free bonds). Kept for
   * back-compat; the active compute() path now uses the proper signed
   * dihedral below.
   */
  static getSimplifiedDihedralFromPositions(p1: Vector3, p2: Vector3, p3: Vector3, p4: Vector3): number {
    const x21 = p2.x - p1.x;
    const y21 = p2.y - p1.y;
    const z21 = p2.z - p1.z;
    const x34 = p3.x - p4.x;
    const y34 = p3.y - p4.y;
    const z34 = p3.z - p4.z;
    const xx = y21 * z34 - z21 * y34;
    const yy = z21 * x34 - x21 * z34;
    const zz = x21 * y34 - y21 * x34;
    return Math.abs(Math.atan2(Math.sqrt(xx * xx + yy * yy + zz * zz), x21 * x34 + y21 * y34 + z21 * z34));
  }

  /**
   * Backward-compatible alias — same simplified angle as before. New code
   * should call getProperDihedralFromPositions when the actual signed
   * IUPAC dihedral is needed.
   */
  static getDihedralFromPositions(p1: Vector3, p2: Vector3, p3: Vector3, p4: Vector3): number {
    return TorsionalBond.getSimplifiedDihedralFromPositions(p1, p2, p3, p4);
  }

  /**
   * Proper SIGNED dihedral phi ∈ [-π, π] between the planes (p1,p2,p3) and
   * (p2,p3,p4). phi = 0 ⇒ eclipsed (cis); phi = ±π ⇒ anti (trans).
   */
  static getProperDihedralFromPositions(p1: Vector3, p2: Vector3, p3: Vector3, p4: Vector3): number {
    const b1x = p2.x - p1.x;
    const b1y = p2.y - p1.y;
    const b1z = p2.z - p1.z;
    const b2x = p3.x - p2.x;
    const b2y = p3.y - p2.y;
    const b2z = p3.z - p2.z;
    const b3x = p4.x - p3.x;
    const b3y = p4.y - p3.y;
    const b3z = p4.z - p3.z;
    // n1 = b1 × b2
    const n1x = b1y * b2z - b1z * b2y;
    const n1y = b1z * b2x - b1x * b2z;
    const n1z = b1x * b2y - b1y * b2x;
    // n2 = b2 × b3
    const n2x = b2y * b3z - b2z * b3y;
    const n2y = b2z * b3x - b2x * b3z;
    const n2z = b2x * b3y - b2y * b3x;
    const b2len = Math.sqrt(b2x * b2x + b2y * b2y + b2z * b2z) || 1e-30;
    // m = (n1 × n2) — used for the sign via b2_hat · m.
    const mx = n1y * n2z - n1z * n2y;
    const my = n1z * n2x - n1x * n2z;
    const mz = n1x * n2y - n1y * n2x;
    const y = (b2x * mx + b2y * my + b2z * mz) / b2len;
    const x = n1x * n2x + n1y * n2y + n1z * n2z;
    return Math.atan2(y, x);
  }

  /** Legacy unsigned simplified dihedral — kept for any external callers. */
  static getDihedral(a1: Atom, a2: Atom, a3: Atom, a4: Atom): number {
    return TorsionalBond.getSimplifiedDihedralFromPositions(a1.position, a2.position, a3.position, a4.position);
  }

  /** Convenience: proper signed dihedral straight from atoms. */
  static getProperDihedral(a1: Atom, a2: Atom, a3: Atom, a4: Atom): number {
    return TorsionalBond.getProperDihedralFromPositions(a1.position, a2.position, a3.position, a4.position);
  }

  atom1: Atom; // end atom
  atom2: Atom; // middle atom
  atom3: Atom; // middle atom
  atom4: Atom; // end atom
  periodicity: number;
  strength: number;
  /**
   * Equilibrium dihedral offset (gamma) in radians. The potential is
   *   V = 0.5 * strength * (1 - cos(periodicity * phi - dihedral))
   * so V = 0 whenever periodicity * phi == dihedral (mod 2π). For sp3 anti
   * minima with periodicity 3, the conventional choice is dihedral = π
   * (cos(3π − π) = cos(2π) = 1 ⇒ V = 0 at phi = π).
   */
  dihedral: number;
  /**
   * The as-built proper dihedral φ₀ (radians): the explicit constructor
   * argument, else the geometry at construction. Unlike `dihedral`, which a
   * parameter pack rewrites to periodicity·φ₀, this never changes once the
   * bond exists, so a pack can be (re-)applied at any later time — after the
   * atoms have moved — and still pin the minimum to the as-built conformation,
   * the same reference RadialBond.length and AngularBond.angle use.
   */
  builtDihedral: number;
  /** [DIAG] last two V evaluations from compute() — for NVE-jump diagnostic. */
  lastEnergy: number = 0;
  prevEnergy: number = 0;

  constructor(atom1: Atom, atom2: Atom, atom3: Atom, atom4: Atom, dihedral?: number) {
    this.atom1 = atom1;
    this.atom2 = atom2;
    this.atom3 = atom3;
    this.atom4 = atom4;
    this.periodicity = TorsionalBond.DEFAULT_PERIODICITY;
    this.strength = TorsionalBond.DEFAULT_STRENGTH;
    // Capture the initial PROPER signed dihedral so the bond is at-rest in
    // its construction-time conformation under the proper-dihedral compute().
    this.builtDihedral =
      dihedral !== undefined ? dihedral : TorsionalBond.getProperDihedral(atom1, atom2, atom3, atom4);
    this.dihedral = this.builtDihedral;
  }

  containsAtom(atom: Atom): boolean {
    return this.atom1 === atom || this.atom2 === atom || this.atom3 === atom || this.atom4 === atom;
  }

  /** Returns the current PROPER signed dihedral. */
  getCurrentDihedral(): number {
    return TorsionalBond.getProperDihedral(this.atom1, this.atom2, this.atom3, this.atom4);
  }

  /**
   * Energy-only: V(phi) = 0.5 * strength * (1 - cos(periodicity * phi - dihedral)),
   * with phi = proper signed dihedral between the (1,2,3) and (2,3,4) planes.
   * No forces applied — compute() falls back to it at degenerate (collinear)
   * configurations, and the tests use it for numerical-gradient checks.
   */
  private computeEnergy(): number {
    const phi = TorsionalBond.getProperDihedralFromPositions(
      this.atom1.position,
      this.atom2.position,
      this.atom3.position,
      this.atom4.position,
    );
    if (!Number.isFinite(phi)) return 0;
    const delta = this.periodicity * phi - this.dihedral;
    return 0.5 * this.strength * (1 - Math.cos(delta));
  }

  /**
   * Compute torsion force + energy on all four atoms.
   *
   * V is the proper-dihedral potential
   *   V(phi) = 0.5 * strength * (1 - cos(periodicity * phi - dihedral))
   * with phi the signed dihedral between the (1,2,3) and (2,3,4) planes.
   * Forces are the closed-form −∇V of Blondel & Karplus (J. Comput. Chem.
   * 17, 1132, 1996), as in GROMACS. With F = p1 − p2, G = p2 − p3,
   * H = p4 − p3, A = F × G, B = H × G (the n1 / n2 of
   * getProperDihedralFromPositions, so the same sign convention):
   *   ∂phi/∂p1 = −|G|/A² · A
   *   ∂phi/∂p4 = +|G|/B² · B
   *   ∂phi/∂p2 = −∂phi/∂p1 + (F·G)/(A²|G|) · A − (H·G)/(B²|G|) · B
   *   ∂phi/∂p3 = −∂phi/∂p4 − (F·G)/(A²|G|) · A + (H·G)/(B²|G|) · B
   * The four sum to zero exactly, and so do the torques: no net force on the
   * molecule. The forward-difference gradient used before did not — its
   * O(h·V'') error is a push toward −x, −y, −z on every atom near a torsion
   * minimum (where V'' ≥ 0), and summed over a polymer's torsions it drove
   * the whole chain toward one corner of the box. torsionalBond.test.ts
   * checks this force against a central-difference −∇V (an earlier analytic
   * decomposition, not this one, failed that check and pumped energy).
   *
   * Acceleration scaling matches the radial / angular bond convention:
   * raw force (eV/Å) is multiplied by GF_CONVERSION_CONSTANT / mass before
   * being added to atom.force, which the integrator treats as acceleration.
   */
  compute(): number {
    if (this.atom1.fixed && this.atom2.fixed && this.atom3.fixed && this.atom4.fixed) return 0;
    if (!this.atom1.force || !this.atom2.force || !this.atom3.force || !this.atom4.force) return 0;

    if (this.strength === 0) return 0;

    const p1 = this.atom1.position;
    const p2 = this.atom2.position;
    const p3 = this.atom3.position;
    const p4 = this.atom4.position;
    const fx = p1.x - p2.x;
    const fy = p1.y - p2.y;
    const fz = p1.z - p2.z;
    const gx = p2.x - p3.x;
    const gy = p2.y - p3.y;
    const gz = p2.z - p3.z;
    const hx = p4.x - p3.x;
    const hy = p4.y - p3.y;
    const hz = p4.z - p3.z;
    // A = F × G, B = H × G
    const ax = fy * gz - fz * gy;
    const ay = fz * gx - fx * gz;
    const az = fx * gy - fy * gx;
    const bx = hy * gz - hz * gy;
    const by = hz * gx - hx * gz;
    const bz = hx * gy - hy * gx;
    const a2 = ax * ax + ay * ay + az * az;
    const b2 = bx * bx + by * by + bz * bz;
    const g2 = gx * gx + gy * gy + gz * gz;
    // Collinear 1-2-3 or 2-3-4 (or coincident 2, 3): phi is undefined there and
    // its gradient blows up as 1/A², 1/B²; apply no force.
    if (a2 < 1e-12 || b2 < 1e-12 || g2 < 1e-12) {
      const v = this.computeEnergy();
      this.prevEnergy = this.lastEnergy;
      this.lastEnergy = v;
      return v;
    }
    // phi as in getProperDihedralFromPositions (G · (B × A) = b2 · (n1 × n2)).
    const gLen = Math.sqrt(g2);
    const phi = Math.atan2(
      (gx * (by * az - bz * ay) + gy * (bz * ax - bx * az) + gz * (bx * ay - by * ax)) / gLen,
      ax * bx + ay * by + az * bz,
    );
    const v0 = 0.5 * this.strength * (1 - Math.cos(this.periodicity * phi - this.dihedral));
    // dV/dphi; the force is −dV/dphi · ∂phi/∂p.
    const dVdPhi = 0.5 * this.strength * this.periodicity * Math.sin(this.periodicity * phi - this.dihedral);
    const fg = fx * gx + fy * gy + fz * gz;
    const hg = hx * gx + hy * gy + hz * gz;
    const ca = gLen / a2; // ∂phi/∂p1 = −ca · A
    const cb = gLen / b2; // ∂phi/∂p4 = +cb · B
    const sa = fg / (a2 * gLen);
    const sb = hg / (b2 * gLen);
    // ∂phi/∂p for each atom
    const d1x = -ca * ax;
    const d1y = -ca * ay;
    const d1z = -ca * az;
    const d4x = cb * bx;
    const d4y = cb * by;
    const d4z = cb * bz;
    const d2x = -d1x + sa * ax - sb * bx;
    const d2y = -d1y + sa * ay - sb * by;
    const d2z = -d1z + sa * az - sb * bz;
    const d3x = -d4x - sa * ax + sb * bx;
    const d3y = -d4y - sa * ay + sb * by;
    const d3z = -d4z - sa * az + sb * bz;
    const add = (atom: Atom, dx: number, dy: number, dz: number) => {
      if (atom.fixed) return;
      const k = (-dVdPhi * GF_CONVERSION_CONSTANT) / atom.mass;
      atom.force!.x += k * dx;
      atom.force!.y += k * dy;
      atom.force!.z += k * dz;
    };
    add(this.atom1, d1x, d1y, d1z);
    add(this.atom2, d2x, d2y, d2z);
    add(this.atom3, d3x, d3y, d3z);
    add(this.atom4, d4x, d4y, d4z);

    // [DIAG] track per-torsion V across two steps for the NVE-jump
    // diagnostic in simulationControls.tsx.
    this.prevEnergy = this.lastEnergy;
    this.lastEnergy = v0;

    return v0;
  }
}
